import type { CrateType, Crate } from '../entities/crate';
import type { Team } from '../logic/team';
import type { Worm } from '../entities/worm';
import type { EntityRegistry } from './entityRegistry';

interface RNG {
  nextFloat: (min: number, max: number) => number;
  next: () => number;
  pick: <T>(arr: T[]) => T;
}

export interface CrateManagerDeps {
  worldWidth: number;
  crateDropChance: number;
  weaponOptions: string[];
  getEntityRegistry: () => EntityRegistry;
  getRNG: () => RNG;
  createCrate: (x: number, y: number, type: CrateType, contents: string) => Crate;
  addCrateEntity: (crate: Crate) => void;
  addCrateSprite: (crate: Crate) => void;
  trackCrate: (crate: Crate) => void;
  findWormById: (id: string) => Worm | null;
  findTeamByWormId: (wormId: string) => Team | null;
  healWorm: (worm: Worm, amount: number) => void;
  addWeaponToTeam: (team: Team, weaponId: string, count: number) => void;
  updateWeaponPanel: (team: Team) => void;
  showDamageNumber: (x: number, y: number, damage: number) => void;
  showCommentary: (type: 'health' | 'weapon' | 'utility') => void;
  showCollectionMessage: (wormName: string, itemName: string) => void;
  playSound: (sound: string) => void;
  shouldShowDamageNumbers: () => boolean;
}

export interface CrateSpawnResult {
  type: CrateType;
  contents: string;
  crate: Crate;
}

export class CrateManager {
  private deps: CrateManagerDeps;

  constructor(deps: CrateManagerDeps) {
    this.deps = deps;
  }

  shouldSpawnCrate(): boolean {
    const rng = this.deps.getRNG();
    return rng.next() < this.deps.crateDropChance;
  }

  spawnRandomCrate(): CrateSpawnResult {
    const rng = this.deps.getRNG();
    const x = rng.nextFloat(100, this.deps.worldWidth - 100);
    const y = -20;

    const typeRoll = rng.next();
    let type: CrateType;
    let contents: string;

    if (typeRoll < 0.3) {
      type = 'health';
      contents = 'health_25';
    } else {
      type = 'weapon';
      contents = rng.pick(this.deps.weaponOptions);
    }

    const crate = this.deps.createCrate(x, y, type, contents);

    this.deps.addCrateEntity(crate);
    this.deps.addCrateSprite(crate);
    this.deps.trackCrate(crate);

    try {
      this.deps.showCommentary(type);
    } catch {}

    return { type, contents, crate };
  }

  addCrate(crate: Crate): void {
    this.deps.addCrateEntity(crate);
    this.deps.addCrateSprite(crate);
  }

  handleCrateCollection(crate: Crate, wormId: string): void {
    const worm = this.deps.findWormById(wormId);
    if (!worm) return;

    if (crate.type === 'health') {
      const healAmount = 25;
      this.deps.healWorm(worm, healAmount);
      this.deps.playSound('heal');
      if (this.deps.shouldShowDamageNumbers()) {
        this.deps.showDamageNumber(worm.position.x, worm.position.y, -healAmount);
      }
      this.deps.showCollectionMessage(worm.name, 'Health +25');
    } else if (crate.type === 'weapon') {
      const team = this.deps.findTeamByWormId(wormId);
      if (team) {
        const weaponId = crate.contents;
        this.deps.addWeaponToTeam(team, weaponId, 1);
        this.deps.updateWeaponPanel(team);
        this.deps.playSound('collectCrate');
        this.deps.showCollectionMessage(worm.name, weaponId);
      }
    }
  }

  canDropCrates(): boolean {
    return this.deps
      .getEntityRegistry()
      .getAll<Crate>('crate')
      .some((c) => c.parachuteOpen);
  }

  dropAllCrates(): void {
    for (const crate of this.deps.getEntityRegistry().getAll<Crate>('crate')) {
      if (crate.parachuteOpen) {
        crate.parachuteOpen = false;
      }
    }
  }
}
